import React, { ReactNode } from 'react';
import { ContentBlock } from '@jbhi-fi/components/src/ContentBlock';
import { BreadcrumbProps, Breadcrumbs } from '../../components/Breadcrumbs/Breadcrumbs';
import { styles } from './GenericPage.css';
import { useGenericPageProps } from '../../hooks/content/useGenericPageProps';
import { Disclaimer } from '@jbhi-fi/components/src/Disclaimer';
import { useBreakpoint } from '@jbhi-fi/lanyard-ui/utils/useBreakpoint';
import { MultiColumnBlock, MultiColumnBlockProps } from '@jbhi-fi/components/src/MultiColumnBlock/MultiColumnBlock';
import { useContentfulAds } from '@jbhi-fi/components/src/hooks/useContentfulAds';
import { usePublicApiClient } from '@jbhi-fi/components/src/hooks/usePublicApiClient';
import { HeroBannerCarousel } from '@jbhi-fi/components/src/HeroBannerCarousel/HeroBannerCarousel';
import { CarouselBannerTransformer } from '@jbhi-fi/components/src/Contentful/transformers/CarouselBannerTransformer';

const MAX_CAROUSEL_BANNERS = 8;

export type GenericPageProps = {
  breadcrumbs: BreadcrumbProps;
  heading: string;
  subheading?: string;
  handle: string;
  heroBackgroundColor?: string;
  heroLogo?: ReactNode;
  heroSection?: 'icon' | 'half' | 'content' | 'full';
  content: ReactNode;
  htmlContent?: string;
  preFooterSectionProps?: MultiColumnBlockProps;
};

export const GenericPage = () => {
  const { data: genericPage, error } = useGenericPageProps();
  const {
    breadcrumbs,
    heading,
    subheading,
    handle,
    content,
    htmlContent,
    heroBackgroundColor,
    heroLogo,
    heroSection,
    preFooterSectionProps,
  } = genericPage as GenericPageProps;
  const isDesktop = useBreakpoint('md', 'up');
  const shouldShowHeroSection = heroSection && heroLogo;

  const { contentfulThemeApiClient, contentfulClient } = usePublicApiClient();
  const { banners, isLoading } = useContentfulAds({
    queryOptions: { handle },
    contentfulThemeApiClient,
    contentfulClient,
  });

  const getPadding = (
    isPaddingTop: boolean,
    heroSection: string | undefined, // The type of hero section
    subheading: string | undefined, // Whether there is a subheading
    heroLogo: ReactNode | undefined // Whether there is a logo in the hero section
  ): { mobile: any; tablet: any } => {
    const isSizeFullOrContent = heroSection === 'full' || heroSection === 'content';
    if (heroSection && heroLogo) {
      return isSizeFullOrContent && !subheading ? { mobile: 0, tablet: 0 } : { mobile: 7.5, tablet: 10 };
    }
    {
      return isPaddingTop ? { mobile: 5, tablet: 10 } : { mobile: 0, tablet: 0 };
    }
  };

  if (error) return <>{error}</>;
  return (
    <>
      <div style={{ backgroundColor: heroSection ? heroBackgroundColor : undefined }}>
        {isDesktop || !heroSection ? <Breadcrumbs className={styles.breadcrumbs} {...breadcrumbs} /> : null}
        <ContentBlock
          paddingTop={getPadding(true, heroSection, subheading, heroLogo)}
          paddingX={{ xs: 0, md: 0 }}
          paddingBottom={getPadding(false, heroSection, subheading, heroLogo)}
          maxWidth={heroSection === 'full' ? 'full' : { mobile: 'xlarge', xxxl: 'ultralarge' }}
        >
          {shouldShowHeroSection ? (
            <div className={styles.logoSize({ heroSection })}>
              {React.isValidElement(heroLogo) && (heroSection === 'full' || heroSection === 'content')
                ? React.cloneElement(heroLogo as React.ReactElement<any>, {
                    className: `${heroLogo.props.className || ''} full-hero-logo`, // Add a custom class for styling
                    sizes: null,
                  })
                : heroLogo}
            </div>
          ) : null}
          <h1 className={styles.heading({ screenReaderOnly: !!shouldShowHeroSection })}>{heading}</h1>
          {subheading ? <p className={styles.subheading}>{subheading}</p> : null}
        </ContentBlock>
      </div>
      {banners?.length ? (
        <HeroBannerCarousel
          {...CarouselBannerTransformer.toHeroBannerCarouselProps(banners.slice(0, MAX_CAROUSEL_BANNERS), {
            variant: 'genericPage',
          })}
          isLoading={isLoading}
          variant="genericPage"
        />
      ) : null}
      <ContentBlock paddingX={0} paddingTop={0} maxWidth="full">
        {content}
      </ContentBlock>
      {htmlContent ? (
        <div className={styles.htmlContent} dangerouslySetInnerHTML={{ __html: htmlContent }}></div>
      ) : null}
      {preFooterSectionProps ? (
        <div style={{ backgroundColor: preFooterSectionProps.theme?.background }}>
          <ContentBlock
            paddingTop={{ xs: 10, md: 15 }}
            paddingBottom={{ xs: 10, md: 15 }}
            maxWidth={{ mobile: 'xlarge', xxxl: 'ultralarge' }}
          >
            <MultiColumnBlock {...preFooterSectionProps} />
          </ContentBlock>
        </div>
      ) : (
        <ContentBlock
          paddingTop={0}
          paddingLeft={0}
          paddingRight={0}
          paddingBottom={{ mobile: 10, tablet: 20 }}
          maxWidth={{ mobile: 'xlarge', xxxl: 'ultralarge' }}
        >
          <ContentBlock
            paddingTop={15}
            paddingBottom={0}
            paddingX={{ xs: 4, md: 0 }}
            maxWidth={{ mobile: 'xlarge', xxxl: 'ultralarge' }}
          >
            <Disclaimer disclaimers={window.Jbhifi.disclaimers} />
          </ContentBlock>
        </ContentBlock>
      )}
    </>
  );
};
